import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';
import OWRadioGroup from '../ow_radio_group/OWRadioGroup';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	renderCurrencyByCurrencyId,
} from '../../utils/DataAccessUtils';

interface PartEquipmentPricePickerProps {
	entityId: any;
	entityName: 'partId' | 'equipmentTypeId';
	vendorId: any;
	fetchMethod: any;
	fallbackCurrencyId: any;
	value: any;
	onChange: (unitPrice: any) => void;
	onCatalogLoad?: (catalog: any, catalogCurrencyId: any) => void;
	disabled?: boolean;
	defaultToListPrice?: boolean;
}

const PartEquipmentPricePicker: FC<PartEquipmentPricePickerProps> = ({
	entityId,
	entityName,
	vendorId,
	fetchMethod,
	fallbackCurrencyId,
	value,
	onChange,
	onCatalogLoad,
	disabled,
	defaultToListPrice,
}) => {
	const [catalog, setCatalog] = useState(null);
	const [catalogLoading, setCatalogLoading] = useState(false);

	const refreshCatalog = useCallback(
		(id, partEquipmentVendorId) => {
			setCatalogLoading(true);
			fetchMethod(
				{
					[entityName]: id,
					partEquipmentVendorId,
				},
				'add_in_pr_index'
			)
				.then((res) => {
					const nextCatalog = res && res.length > 0 ? res[0] : null;
					setCatalog(nextCatalog);
				})
				.finally(() => {
					setCatalogLoading(false);
				});
		},
		[entityName, fetchMethod]
	);

	useEffect(() => {
		if (entityId && vendorId) {
			refreshCatalog(entityId, vendorId);
		} else {
			setCatalog(null);
		}
	}, [entityId, refreshCatalog, vendorId]);

	const catalogCurrencyId = useMemo(
		() => nullSafeGetOrElse('partEquipmentVendor.currencyId', catalog, fallbackCurrencyId),
		[catalog, fallbackCurrencyId]
	);

	useEffect(() => {
		if (!catalogLoading && onCatalogLoad) {
			onCatalogLoad(catalog, catalogCurrencyId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalog, catalogCurrencyId, catalogLoading]);

	const listPrice = nullSafeGet('listPrice', catalog);
	const lastPrice = nullSafeGet('lastPrice', catalog);

	// When defaultToListPrice is enabled, auto-select List Price the moment a
	// catalog containing one loads — but only if the consumer hasn't already
	// set a value. Intentionally depends on `catalog` only so that clearing or
	// manually changing `value` later does NOT re-fire this effect.
	useEffect(() => {
		if (!defaultToListPrice) return;
		if (catalogLoading) return;
		if (!listPrice) return;
		const valueIsEmpty = value === undefined || value === null || value === '';
		if (!valueIsEmpty) return;
		onChange(parseFloat(listPrice));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalog]);

	const selectedValue = useMemo(() => {
		if (value === undefined || value === null || value === '') return null;
		const numericValue = parseFloat(value);
		if (isNaN(numericValue)) return null;
		if (listPrice && parseFloat(listPrice) === numericValue) return numericValue;
		if (lastPrice && parseFloat(lastPrice) === numericValue) return numericValue;
		return null;
	}, [lastPrice, listPrice, value]);

	const onPriceSelect = useCallback(
		(unitPrice) => {
			onChange(unitPrice);
		},
		[onChange]
	);

	if (catalogLoading) {
		return (
			<div className="ml-2">
				<Spin size="small" />
			</div>
		);
	}

	if (!listPrice && !lastPrice) {
		return null;
	}

	return (
		<div className="ml-2">
			<OWRadioGroup
				value={selectedValue}
				onChange={onPriceSelect}
				disabled={disabled}
				valueAccessor={(item) => item.value}
				renderRecord={(item) => item.label}
				items={[
					...(listPrice
						? [
								{
									label: `List Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(listPrice)}`,
									value: parseFloat(listPrice),
								},
						  ]
						: []),
					...(lastPrice
						? [
								{
									label: `Last Price ${renderCurrencyByCurrencyId(catalogCurrencyId)(lastPrice)}`,
									value: parseFloat(lastPrice),
								},
						  ]
						: []),
				]}
			/>
		</div>
	);
};

export default PartEquipmentPricePicker;
